// /js/pages/login.js
// Login page controller (MPA — runs on page load, no initX wrapper).

import { supabase } from '/js/services/supabaseClient.js';
import { showToast } from '/js/modules/toast.js';

console.log('📌 login.js loaded');

// ============================================
// BOOT
// ============================================
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initLogin);
} else {
    initLogin();
}

function initLogin() {
    console.log('🔐 Login page initializing');

    const form = document.getElementById('loginForm');
    if (form) {
        form.addEventListener('submit', handleLogin);
    }

    const forgot = document.getElementById('forgotPasswordLink');
    if (forgot) {
        forgot.addEventListener('click', (e) => {
            e.preventDefault();
            showForgotPassword();
        });
    }

    // If already logged in, offer a shortcut
    supabase.auth.getSession().then(({ data: { session } }) => {
        if (session?.user) {
            // Already authenticated — show a subtle notice instead of forcing redirect
            const formEl = document.getElementById('loginForm');
            if (formEl && !document.getElementById('alreadyLoggedInNotice')) {
                const notice = document.createElement('div');
                notice.id = 'alreadyLoggedInNotice';
                notice.className = 'mb-4 p-3 rounded-lg text-sm text-center';
                notice.style.background = '#FEF3C7';
                notice.style.color = '#92400E';
                notice.innerHTML =
                    `You're already signed in as <strong>${escapeHtml(session.user.email)}</strong>. ` +
                    `<a href="/" style="color:#C41E24; text-decoration:underline;">Go to Home</a> ` +
                    `or sign in as a different user below.`;
                formEl.parentNode.insertBefore(notice, formEl);
            }
        }
    });
}

// ============================================
// SUBMIT HANDLER
// ============================================
async function handleLogin(e) {
    e.preventDefault();

    const email       = document.getElementById('loginEmail').value.trim();
    const password    = document.getElementById('loginPassword').value;
    const btn         = document.getElementById('loginBtn');
    const btnText     = document.getElementById('loginBtnText');
    const btnSpinner  = document.getElementById('loginBtnSpinner');

    if (!email || !password) {
        showToast('Please fill in all fields', 'warning');
        return;
    }

    btn.disabled = true;
    btnText.textContent = 'Logging in...';
    btnSpinner.classList.remove('hidden');

    try {
        const { data, error } = await supabase.auth.signInWithPassword({ email, password });

        if (error) {
            showToast(friendlyAuthError(error), 'error');
            return;
        }

        if (data.user && data.session) {
            console.log('✅ Login successful:', data.user.email);
            showToast('Login successful! Welcome back.', 'success');

            // MPA-safe redirect (no more App.navigate)
            const redirectPage = sessionStorage.getItem('redirectAfterLogin');
            const redirectParams = sessionStorage.getItem('redirectParams');

            let target = '/';
            if (redirectPage) {
                target = redirectPage.startsWith('/') ? redirectPage : `/${redirectPage}`;
                if (redirectParams) {
                    try {
                        const params = JSON.parse(redirectParams);
                        const qs = new URLSearchParams(params).toString();
                        if (qs) target += `?${qs}`;
                    } catch { /* ignore bad JSON */ }
                }
                sessionStorage.removeItem('redirectAfterLogin');
                sessionStorage.removeItem('redirectParams');
            }

            setTimeout(() => { window.location.href = target; }, 500);
        } else {
            showToast('Login failed. Please try again.', 'error');
        }
    } catch (err) {
        console.error('Login error:', err);
        showToast('An error occurred: ' + err.message, 'error');
    } finally {
        btn.disabled = false;
        btnText.textContent = 'Login';
        btnSpinner.classList.add('hidden');
    }
}

// ============================================
// FORGOT PASSWORD
// ============================================
async function showForgotPassword() {
    const email = document.getElementById('loginEmail')?.value?.trim() || '';

    if (!email) {
        showToast('Please enter your email address first', 'warning');
        return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
        showToast('Please enter a valid email address', 'warning');
        return;
    }

    const confirmed = confirm(`Send password reset link to ${email}?`);
    if (!confirmed) return;

    try {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: `${window.location.origin}/reset-password`,
        });

        if (error) {
            if (error.message.toLowerCase().includes('not found')) {
                showToast('No account found with this email address.', 'error');
            } else {
                showToast('Error sending reset email: ' + error.message, 'error');
            }
            return;
        }

        showToast(`✅ Password reset link sent to ${email}. Check your inbox.`, 'success');
    } catch (err) {
        console.error('Password reset error:', err);
        showToast('An error occurred: ' + err.message, 'error');
    }
}

// ============================================
// HELPERS
// ============================================
function friendlyAuthError(error) {
    const msg = (error.message || '').toLowerCase();
    if (msg.includes('invalid login credentials')) {
        return 'Invalid email or password. Please try again.';
    }
    if (msg.includes('email not confirmed')) {
        return 'Please verify your email before logging in. Check your inbox.';
    }
    if (msg.includes('too many requests') || msg.includes('rate limit')) {
        return 'Too many login attempts. Please wait a moment and try again.';
    }
    return error.message;
}

function escapeHtml(s) {
    return String(s ?? '').replace(/[&<>"']/g, c => ({
        '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'
    }[c]));
}